if (!customElements.get('multi-background-feature-story')) {
  customElements.define('multi-background-feature-story', class MultiBackgroundFeatureStory extends HTMLElement {
    constructor() {
      super();
      this.animationFrame = null;
      this.scrollFrameRequested = false;
      this.currentProgress = 0;
      this.targetProgress = 0;
      this.scrollDistance = 1;
      this.sectionTop = 0;
      this.isEnhanced = false;
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleModeChange = this.handleModeChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      this.handleSectionUnload = this.handleSectionUnload.bind(this);
      this.animate = this.animate.bind(this);
    }

    connectedCallback() {
      this.section = this.closest('[data-multi-background-feature-story-section]');
      this.viewport = this.querySelector('.multi-background-feature-story__viewport');
      this.allStages = Array.from(this.querySelectorAll('[data-stage]'));
      this.stageMedia = Array.from(this.querySelectorAll('[data-stage-media]'));
      this.stages = [];
      this.mediaQuery = window.matchMedia('(min-width: 750px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (!this.section || !this.viewport || this.allStages.length === 0) return;

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('shopify:section:load', this.handleSectionLoad);
      document.addEventListener('shopify:section:unload', this.handleSectionUnload);
      this.mediaQuery.addEventListener('change', this.handleModeChange);
      this.reducedMotionQuery.addEventListener('change', this.handleModeChange);

      this.applyMode();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:section:load', this.handleSectionLoad);
      document.removeEventListener('shopify:section:unload', this.handleSectionUnload);

      if (this.mediaQuery) this.mediaQuery.removeEventListener('change', this.handleModeChange);
      if (this.reducedMotionQuery) this.reducedMotionQuery.removeEventListener('change', this.handleModeChange);
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.scrollFrameRequested = false;
    }

    get canEnhance() {
      const mobileAnimationEnabled = this.section.dataset.enableMobileAnimation !== 'false';
      return (this.mediaQuery.matches || mobileAnimationEnabled) && this.stages.length > 0;
    }

    applyMode() {
      this.refreshStages();
      this.isEnhanced = this.canEnhance;
      this.section.classList.toggle('is-enhanced', this.isEnhanced);
      this.classList.toggle('is-enhanced', this.isEnhanced);
      this.section.style.setProperty('--multi-background-feature-story-stage-count', String(this.stages.length));
      const totalScrollLength = this.getTimeline().totalDuration;
      this.section.style.setProperty('--multi-background-feature-story-stage-total-scroll-length', `${totalScrollLength}vh`);
      this.measure();

      if (this.isEnhanced) {
        this.updateTarget();
        this.currentProgress = this.targetProgress;
        this.applyProgress();
        return;
      }

      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.currentProgress = 0;
      this.targetProgress = 0;
      this.applyStaticState();
    }

    refreshStages() {
      const currentDevice = this.mediaQuery.matches ? 'desktop' : 'mobile';
      this.stages = this.allStages.filter((stage) => {
        const device = stage.dataset.stageDevice || 'all';
        const isVisible = device === 'all' || device === currentDevice;
        stage.hidden = !isVisible;

        if (!isVisible) {
          stage.classList.remove('is-active');
          stage.setAttribute('aria-hidden', 'true');
          stage.inert = true;
          stage.style.removeProperty('--multi-background-feature-story-stage-opacity');
          stage.style.removeProperty('--multi-background-feature-story-stage-offset');
          const media = this.stageMedia.find((item) => item.dataset.stageIndex === stage.dataset.stageIndex);
          if (media) {
            media.classList.remove('is-active');
            media.style.opacity = '0';
          }
        }

        return isVisible;
      });

      this.stages.forEach((stage, index) => {
        stage.dataset.activeStageIndex = String(index);
      });
    }

    measure() {
      if (!this.section || !this.viewport) return;
      const sectionRect = this.section.getBoundingClientRect();
      this.sectionTop = sectionRect.top + window.scrollY;
      this.scrollDistance = Math.max(1, this.section.offsetHeight - this.viewport.offsetHeight);
    }

    handleScroll() {
      if (!this.isEnhanced || this.scrollFrameRequested) return;
      this.scrollFrameRequested = true;
      window.requestAnimationFrame(() => {
        this.scrollFrameRequested = false;
        if (!this.isEnhanced) return;
        this.updateTarget();
        this.requestAnimationFrame();
      });
    }

    handleResize() {
      this.applyMode();
    }

    handleModeChange() {
      this.applyMode();
    }

    updateTarget() {
      const rawProgress = (window.scrollY - this.sectionTop) / this.scrollDistance;
      this.targetProgress = Math.min(1, Math.max(0, rawProgress));
    }

    requestAnimationFrame() {
      if (this.animationFrame) return;
      this.animationFrame = window.requestAnimationFrame(this.animate);
    }

    animate() {
      if (this.reducedMotionQuery.matches) {
        this.currentProgress = this.targetProgress;
        this.applyProgress();
        this.animationFrame = null;
        return;
      }

      const difference = this.targetProgress - this.currentProgress;
      this.currentProgress += difference * 0.12;

      if (Math.abs(difference) < 0.0005) {
        this.currentProgress = this.targetProgress;
      }

      this.applyProgress();
      this.animationFrame = this.currentProgress === this.targetProgress
        ? null
        : window.requestAnimationFrame(this.animate);
    }

    applyProgress() {
      if (this.stages.length === 0) return;
      const lastIndex = this.stages.length - 1;

      if (lastIndex === 0) {
        this.applyStaticState();
        return;
      }

      const timeline = this.getTimeline();
      const totalDuration = timeline.totalDuration;
      const timelinePosition = this.currentProgress * totalDuration;
      const viewportHeight = this.viewport.offsetHeight;
      const viewportScale = viewportHeight / 1080;
      const configuredEnterPosition = Number.parseFloat(
        this.mediaQuery.matches
          ? this.section.dataset.contentEnterPositionDesktop
          : this.section.dataset.contentEnterPositionMobile
      );
      const enterPosition = Number.isFinite(configuredEnterPosition) ? configuredEnterPosition : 50;
      const finalTop = Number.parseFloat(window.getComputedStyle(this.stages[0]).paddingTop) || 0;
      const configuredEnterTop = viewportHeight * enterPosition / 100;
      let activeIndex = lastIndex;

      this.stages.forEach((stage, index) => {
        const timing = timeline.stages[index];
        const stageStart = timing.start;
        const localTime = timelinePosition - stageStart;
        const isLast = index === lastIndex;
        const enterDuration = timing.enterDuration;
        const holdDuration = timing.holdDuration;
        const leaveDuration = timing.leaveDuration;
        const stageDuration = timing.duration;
        let opacity = 0;
        // 原位浮现（fade）只做透明度变化，不产生任何位移
        const stageEnterAnimation = this.mediaQuery.matches
          ? stage.dataset.enterAnimationDesktop
          : stage.dataset.enterAnimationMobile;
        const isFadeInPlace = stageEnterAnimation === 'fade';
        // Entry motion must always travel upward. If the configured start point
        // is above the final position, keep a small positive offset instead of
        // reversing the animation direction.
        let offset = isFadeInPlace ? 0 : Math.max(1, configuredEnterTop - finalTop);

        if (enterDuration === 0 && localTime >= 0 && localTime < holdDuration) {
          opacity = 1;
          offset = 0;
          activeIndex = index;
        } else if (localTime >= 0 && localTime < enterDuration) {
          const progress = localTime / enterDuration;
          opacity = progress;
          offset = isFadeInPlace ? 0 : offset * (1 - progress);
          activeIndex = index;
        } else if (localTime >= enterDuration && localTime < enterDuration + holdDuration) {
          opacity = 1;
          offset = 0;
          activeIndex = index;
        } else if (!isLast && localTime >= enterDuration + holdDuration && localTime < stageDuration) {
          const progress = (localTime - enterDuration - holdDuration) / leaveDuration;
          opacity = 1 - progress;
          offset = isFadeInPlace ? 0 : -70 * viewportScale * progress;
          activeIndex = index;
        } else if (isLast && localTime >= enterDuration) {
          opacity = 1;
          offset = 0;
          activeIndex = index;
        } else if (localTime >= stageDuration) {
          offset = isFadeInPlace ? 0 : -70 * viewportScale;
        }

        const easedOpacity = opacity * opacity * (3 - (2 * opacity));
        const isActive = opacity > 0.001;

        stage.style.setProperty('--multi-background-feature-story-stage-opacity', easedOpacity.toFixed(4));
        stage.style.setProperty('--multi-background-feature-story-stage-offset', `${offset.toFixed(2)}px`);
        const media = this.stageMedia.find((item) => item.dataset.stageIndex === stage.dataset.stageIndex);
        if (media) {
          media.style.opacity = easedOpacity.toFixed(4);
          media.classList.toggle('is-active', isActive);
        }
        stage.classList.toggle('is-active', isActive);
        stage.setAttribute('aria-hidden', String(!isActive));
        stage.inert = !isActive;
      });

      this.section.dataset.activeStage = String(activeIndex);
    }

    getTimeline() {
      const enterDistance = 40;
      const leaveDistance = 30;
      let start = 0;
      const stages = this.stages.map((stage, index) => {
        const configuredHold = Number.parseFloat(
          this.mediaQuery.matches ? stage.dataset.holdDistanceDesktop : stage.dataset.holdDistanceMobile
        );
        const holdDuration = Number.isFinite(configuredHold) ? Math.max(0, configuredHold) : 100;
        const enterAnimation = this.mediaQuery.matches
          ? stage.dataset.enterAnimationDesktop
          : stage.dataset.enterAnimationMobile;
        const enterDuration = index === 0 || enterAnimation === 'none' ? 0 : enterDistance;
        const leaveDuration = index === this.stages.length - 1 ? 0 : leaveDistance;
        const duration = enterDuration + holdDuration + leaveDuration;
        const timing = { start, enterDuration, holdDuration, leaveDuration, duration };
        start += duration;
        return timing;
      });

      return { stages, totalDuration: Math.max(1, start) };
    }

    applyStaticState() {
      this.stages.forEach((stage) => {
        stage.style.removeProperty('--multi-background-feature-story-stage-opacity');
        stage.style.removeProperty('--multi-background-feature-story-stage-offset');
        stage.classList.add('is-active');
        stage.setAttribute('aria-hidden', 'false');
        stage.inert = false;
      });
      this.stageMedia.forEach((media, index) => {
        const isActive = index === 0;
        media.style.opacity = isActive ? '1' : '0';
        media.classList.toggle('is-active', isActive);
      });
    }

    handleSectionLoad(event) {
      if (event.target !== this.section) return;
      this.applyMode();
    }

    handleSectionUnload(event) {
      if (event.target !== this.section) return;
      if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }

    handleBlockSelect(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || !this.section || !this.section.contains(target)) return;

      const block = target.closest('[data-shopify-editor-block]');
      const stage = block ? block.closest('[data-stage]') : target.closest('[data-stage]');
      if (!stage || !this.section.contains(stage)) return;

      if (!this.isEnhanced) {
        stage.scrollIntoView({ behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth', block: 'center' });
        return;
      }

      this.measure();
      const stageIndex = Number.parseInt(stage.dataset.activeStageIndex, 10);
      const timeline = this.getTimeline();
      const timing = Number.isFinite(stageIndex) ? timeline.stages[stageIndex] : null;
      const targetProgress = timing
        ? Math.min(1, Math.max(0, (timing.start + timing.enterDuration) / timeline.totalDuration))
        : 0;
      window.scrollTo({
        top: this.sectionTop + (this.scrollDistance * targetProgress),
        behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth'
      });
    }
  });
}
